import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, Panel, Badge, Table, Button, TextInput,
} from "../components/UIKit";
import { Play, RotateCcw, Terminal } from "lucide-react";

export default function AdminScheduler() {
  const { api } = useAdminAuth();
  const [jobs, setJobs] = useState([]);
  const [selId, setSelId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [userId, setUserId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const load = async () => {
    const d = await api("/jobs");
    setJobs(d.items || []);
    if (!selId && d.items?.length) setSelId(d.items[0].id);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!selId) return;
    api(`/jobs/${selId}/runs`).then((d) => setRuns(d.items || []));
  }, [selId, api]);

  const runNow = async () => {
    if (!selId) return;
    setBusy(true);
    setToast("");
    try {
      const res = await api(`/jobs/${selId}/run`, {
        method: "POST",
        data: {
          user_id: userId || null,
          session_id: sessionId || null,
        },
      });
      setToast(
        res.run.status === "completed"
          ? `Completato in ${res.run.duration_ms}ms`
          : `Fallito: ${res.run.error}`
      );
      await load();
      const d = await api(`/jobs/${selId}/runs`);
      setRuns(d.items || []);
    } finally {
      setBusy(false);
      setTimeout(() => setToast(""), 4000);
    }
  };

  const sel = jobs.find((j) => j.id === selId);

  return (
    <div className="p-8">
      <PageHeader
        title="Scheduler"
        subtitle="Job di elaborazione: trigger, stato, esecuzione manuale"
      />

      <div className="grid grid-cols-[380px,1fr] gap-4">
        <Panel title="Job configurati">
          <div className="space-y-1.5">
            {jobs.map((j) => (
              <button
                key={j.id}
                onClick={() => setSelId(j.id)}
                className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                  selId === j.id
                    ? "bg-[#14c2a1]/10 border-[#14c2a1]/30"
                    : "bg-white/[0.03] border-white/10 hover:bg-white/5"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-white text-[13px] font-semibold">
                    {j.name}
                  </div>
                  <Badge
                    color={
                      j.last_run_status === "completed"
                        ? "teal"
                        : j.last_run_status === "failed"
                        ? "rose"
                        : j.last_run_status === "running"
                        ? "amber"
                        : "slate"
                    }
                  >
                    {j.last_run_status || j.status}
                  </Badge>
                </div>
                <div className="text-white/55 text-[11px] mt-0.5 font-mono">
                  {j.trigger === "cron" ? j.cron : j.trigger}
                </div>
              </button>
            ))}
          </div>
        </Panel>

        {sel && (
          <div className="space-y-4">
            <Panel
              title={sel.name}
              right={
                <div className="flex items-center gap-2">
                  <Badge color="slate">{sel.trigger}</Badge>
                  <span className="text-white/55 text-[11px] font-mono">
                    {sel.cron || sel.event || "—"}
                  </span>
                </div>
              }
            >
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <div className="text-white/55 text-[11px] uppercase tracking-wider">
                    Algoritmo
                  </div>
                  <div className="text-white text-[13px] mt-0.5 font-mono">
                    {sel.algorithm_id || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-white/55 text-[11px] uppercase tracking-wider">
                    Ultima esecuzione
                  </div>
                  <div className="text-white text-[13px] mt-0.5">
                    {sel.last_run_at
                      ? new Date(sel.last_run_at).toLocaleString("it-IT")
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-white/55 text-[11px] uppercase tracking-wider">
                    Durata
                  </div>
                  <div className="text-white text-[13px] mt-0.5 font-mono">
                    {sel.last_run_duration_ms
                      ? `${sel.last_run_duration_ms}ms`
                      : "—"}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-white/[0.04] p-3">
                <div className="text-white/70 text-[12px] mb-2 flex items-center gap-1">
                  <Terminal size={12} /> Esegui manualmente
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <TextInput
                    placeholder="user_id (opzionale)"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                  <TextInput
                    placeholder="session_id (opzionale)"
                    value={sessionId}
                    onChange={(e) => setSessionId(e.target.value)}
                  />
                  <Button onClick={runNow} disabled={busy}>
                    <Play size={13} /> {busy ? "In esecuzione…" : "Avvia job"}
                  </Button>
                  {toast && (
                    <span className="text-[12px] text-white/80">{toast}</span>
                  )}
                </div>
              </div>
            </Panel>

            <Panel
              title="Log ultime esecuzioni"
              right={
                <button
                  className="text-[#67e8d1] text-[12px] font-medium flex items-center gap-1"
                  onClick={async () => {
                    const d = await api(`/jobs/${selId}/runs`);
                    setRuns(d.items || []);
                  }}
                >
                  <RotateCcw size={12} /> Aggiorna
                </button>
              }
            >
              <Table
                columns={[
                  {
                    key: "started_at",
                    label: "Inizio",
                    render: (r) =>
                      new Date(r.started_at).toLocaleString("it-IT"),
                  },
                  {
                    key: "status",
                    label: "Stato",
                    render: (r) => (
                      <Badge
                        color={r.status === "completed" ? "teal" : "rose"}
                      >
                        {r.status}
                      </Badge>
                    ),
                  },
                  {
                    key: "duration_ms",
                    label: "Durata",
                    render: (r) => (
                      <span className="font-mono">{r.duration_ms}ms</span>
                    ),
                  },
                  {
                    key: "records_processed",
                    label: "Record",
                    render: (r) => (
                      <span className="font-mono">{r.records_processed}</span>
                    ),
                  },
                  {
                    key: "error",
                    label: "Errore",
                    render: (r) => (
                      <span className="text-rose-300 text-[11.5px]">
                        {r.error || "—"}
                      </span>
                    ),
                  },
                ]}
                rows={runs}
              />
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}
